import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";

export default function About() {
  return (
    <section className="bg-white text-black">
      {/* ================= HERO SECTION ================= */}
      <div className="max-w-7xl mx-auto px-6 py-20 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-3xl sm:text-4xl md:text-5xl font-bold"
        >
          About <span className="text-yellow-600">Hawkins & Brooks</span>
        </motion.h1>

        <p className="mt-6 max-w-3xl mx-auto text-gray-700 text-sm sm:text-base leading-relaxed">
          A professional advisory firm delivering sophisticated corporate, regulatory, and cross-border advisory solutions to businesses operating in an increasingly complex global environment.
        </p>
      </div>

      {/* ================= ABOUT CONTENT ================= */}
      <div className="max-w-7xl mx-auto px-6 pb-20 grid md:grid-cols-2 gap-14 items-center">
        
        {/* LEFT CONTENT */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-2xl sm:text-3xl font-semibold mb-4">
            Who We Are
          </h2>

          <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-4">
            Hawkins & Brooks is a professional advisory firm delivering sophisticated corporate, regulatory, and cross-border advisory solutions to businesses operating in an increasingly complex global environment.
          </p>

          <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-4">
            We advise corporations, startups, investors, and international enterprises across the full business lifecycle — from market entry and incorporation to ongoing compliance, governance, and strategic transactions. Our approach combines deep regulatory knowledge with commercial insight, enabling clients to navigate jurisdictional complexities with confidence and precision.
          </p>

          <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
            With a strong focus on international compliance standards and best-practice governance, Hawkins & Brooks acts as a trusted partner to organizations seeking clarity, structure, and long-term sustainability in their operations.
          </p>
        </motion.div>

        {/* RIGHT HIGHLIGHTS - OUR VALUES */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7 }}
          className="bg-yellow-50 rounded-2xl p-8"
        >
          <h3 className="text-xl font-semibold mb-6">
            Our Values
          </h3>

          <ul className="space-y-4">
            {[
              { title: "Integrity", desc: "Unwavering commitment to ethical conduct and transparency" },
              { title: "Excellence", desc: "Precision, depth, and quality in every engagement" },
              { title: "Client Partnership", desc: "Advisory built on trust, insight, and long-term relationships" },
              { title: "Global Perspective", desc: "International outlook with jurisdiction-specific expertise" },
              { title: "Confidentiality", desc: "Absolute discretion in all client matters" },
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle size={20} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-sm sm:text-base font-semibold text-black block">
                    {item.title}:
                  </span>
                  <span className="text-sm text-gray-700">
                    {item.desc}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>

      {/* ================= MISSION & VISION ================= */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12">
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-2xl font-semibold mb-4 text-yellow-600">
              Our Mission
            </h3>
            <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-4">
              To deliver reliable, globally aligned advisory solutions that empower businesses to establish, operate, and grow with regulatory certainty, strategic clarity, and uncompromising integrity.
            </p>
            <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
              We are committed to simplifying complex compliance landscapes while supporting informed decision-making and sustainable growth for our clients worldwide.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h3 className="text-2xl font-semibold mb-4 text-yellow-600">
              Our Vision
            </h3>
            <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-4">
              To be a trusted international advisory firm recognized for excellence in corporate governance, regulatory compliance, and cross-border advisory — setting the benchmark for professionalism, precision, and client trust.
            </p>
            <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
              We aspire to partner with businesses across borders, enabling them to operate seamlessly in a regulated global economy while upholding the highest standards of ethics and governance.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
